"use client"

import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/lib/auth-context"

export function QueryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  // Replace the cache and mounted page state before a different identity renders.
  const sessionKey = user ? `${user.tenantId ?? "platform"}:${user.id}` : "signed-out"
  return <SessionQueryProvider key={sessionKey}>{children}</SessionQueryProvider>
}

function SessionQueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Default cache: refetch on focus/mount only when stale (not every tab switch).
            staleTime: 60_000,
            gcTime: 5 * 60_000,
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            retry: 1,
          },
        },
      }),
  )

  // Discard the previous identity's queries/mutations, including pending queries.
  useEffect(() => () => queryClient.clear(), [queryClient])

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}
